import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import QRCode from "qrcode";
import { Copy, CheckCircle2, Printer, ShieldCheck } from "lucide-react";
import type { z } from "zod";

import {
  mfaVerifyRequestSchema,
  type MfaSetupResponse,
  type MfaVerifyRequest,
} from "@sendsms/shared";
import { api, ApiError } from "@/lib/api";
import {
  applyFieldErrors,
  translateApiError,
  translateFieldError,
} from "@/lib/form-errors";
import { meQueryKey } from "@/lib/auth/context";
import {
  Card,
  CardHeader,
  CardTitle,
  CardSubtitle,
  CardBody,
} from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";

type Step = "qr" | "codes" | "verify" | "done";
type MfaVerifyFormValues = z.input<typeof mfaVerifyRequestSchema>;

export default function MfaSetupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>("qr");
  const [setup, setSetup] = useState<MfaSetupResponse | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState(false);
  const [topError, setTopError] = useState<string | null>(null);

  const setupMutation = useMutation({
    mutationFn: () => api.mfaSetup(),
    onSuccess: (data) => {
      setSetup(data);
      QRCode.toDataURL(data.otpauth_uri, {
        width: 256,
        margin: 1,
        color: { dark: "#1D1D1F", light: "#FFFFFF" },
      })
        .then(setQrDataUrl)
        .catch(() => setQrDataUrl(null));
    },
    onError: async (err: unknown) => {
      // If MFA is already enabled, the user shouldn't be on this page.
      // Refresh /me and bounce to /dashboard — RequireMfa will redirect to
      // /mfa/verify if the current session is pre-MFA.
      if (err instanceof ApiError && err.code === "mfa_already_enabled") {
        await queryClient.invalidateQueries({ queryKey: meQueryKey });
        navigate("/dashboard", { replace: true });
        return;
      }
      setTopError(translateApiError(t, err));
    },
  });

  // MFA-1 (29-Sep-2026): the server is now idempotent for 30 min while a setup is pending (same QR on refresh /
  // remount); this guard only avoids a redundant call in StrictMode.
  // Guard against StrictMode double-invocation. /mfa/setup is non-idempotent
  // server-side: a second call overwrites the secret with a new one, breaking
  // the QR the user just scanned.
  const firedRef = useRef(false);
  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    setupMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyForm = useForm<MfaVerifyFormValues>({
    resolver: zodResolver(mfaVerifyRequestSchema),
    defaultValues: { code: "", trust_device: false },
    mode: "onSubmit",
  });

  const verifyMutation = useMutation({
    mutationFn: (body: MfaVerifyRequest) => api.mfaVerify(body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: meQueryKey });
      setStep("done");
    },
    onError: (err: unknown) => {
      const handled = applyFieldErrors(t, err, verifyForm.setError);
      if (!handled) setTopError(translateApiError(t, err));
    },
  });

  const copyToClipboard = async (text: string, onCopy: () => void) => {
    try {
      await navigator.clipboard.writeText(text);
      onCopy();
      setTimeout(() => {
        setCopiedSecret(false);
        setCopiedCodes(false);
      }, 2500);
    } catch {
      // fallback
    }
  };

  // Loading initial /setup call (or about to redirect after mfa_already_enabled)
  if (setupMutation.isPending && !setup) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("auth.mfa_setup.title")}</CardTitle>
        </CardHeader>
        <CardBody>
          <div className="flex items-center justify-center py-8">
            <div
              className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-brand-500"
              aria-hidden="true"
            />
          </div>
        </CardBody>
      </Card>
    );
  }

  // mfa_already_enabled error → already redirecting via onError. Show spinner.
  if (
    setupMutation.isError &&
    setupMutation.error instanceof ApiError &&
    setupMutation.error.code === "mfa_already_enabled"
  ) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("auth.mfa_setup.title")}</CardTitle>
        </CardHeader>
        <CardBody>
          <div className="flex items-center justify-center py-8">
            <div
              className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-brand-500"
              aria-hidden="true"
            />
          </div>
        </CardBody>
      </Card>
    );
  }

  if (!setup) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("auth.mfa_setup.title")}</CardTitle>
          <CardSubtitle>
            {topError ?? t("errors.generic")}
          </CardSubtitle>
        </CardHeader>
      </Card>
    );
  }

  if (step === "done") {
    return (
      <Card>
        <CardHeader>
          <div className="mb-2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-success-50">
            <ShieldCheck className="h-6 w-6 text-success-500" />
          </div>
          <CardTitle>{t("auth.mfa_setup.success_title")}</CardTitle>
          <CardSubtitle>{t("auth.mfa_setup.success_message")}</CardSubtitle>
        </CardHeader>
        <CardBody>
          <Button
            fullWidth
            size="lg"
            onClick={() => navigate("/dashboard", { replace: true })}
          >
            {t("common.continue")}
          </Button>
        </CardBody>
      </Card>
    );
  }

  if (step === "qr") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("auth.mfa_setup.title")}</CardTitle>
          <CardSubtitle>{t("auth.mfa_setup.subtitle")}</CardSubtitle>
        </CardHeader>
        <CardBody className="space-y-6">
          {setup.rotated_pending ? (
            <Alert tone="warning" description={t("auth.mfa_setup.rotated_pending")} />
          ) : null}
          <div className="flex flex-col items-center">
            {qrDataUrl ? (
              <img
                src={qrDataUrl}
                alt="MFA setup QR code"
                className="rounded-md border border-border"
                width={256}
                height={256}
              />
            ) : (
              <div className="h-64 w-64 rounded-md bg-surface-subtle" />
            )}
          </div>

          <div>
            <div className="text-caption font-medium text-ink">
              {t("auth.mfa_setup.manual_entry_title")}
            </div>
            <div className="mt-2 flex items-stretch gap-2">
              <div className="flex-1 overflow-x-auto rounded-md bg-surface-subtle px-3.5 py-2.5 font-mono text-caption text-ink">
                {setup.secret_base32}
              </div>
              <Button
                variant="secondary"
                size="md"
                leadingIcon={
                  copiedSecret ? (
                    <CheckCircle2 className="h-4 w-4 text-success-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )
                }
                onClick={() =>
                  copyToClipboard(setup.secret_base32, () =>
                    setCopiedSecret(true),
                  )
                }
              >
                {copiedSecret
                  ? t("auth.mfa_setup.secret_copied")
                  : t("common.copy")}
              </Button>
            </div>
          </div>

          <Button fullWidth size="lg" onClick={() => setStep("codes")}>
            {t("common.continue")}
          </Button>
        </CardBody>
      </Card>
    );
  }

  if (step === "codes") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("auth.mfa_setup.backup_codes_title")}</CardTitle>
          <CardSubtitle>{t("auth.mfa_setup.backup_codes_subtitle")}</CardSubtitle>
        </CardHeader>
        <CardBody className="space-y-5">
          <Alert
            tone="warning"
            description={t("auth.mfa_setup.backup_codes_warning")}
          />
          {setup.backup_codes === null ? (
            <Alert tone="warning" description={t("auth.mfa_setup.backup_codes_reused")} />
          ) : null}

          <div
            className="grid grid-cols-2 gap-2 rounded-md border border-border bg-surface-subtle p-4 print:border-0 print:bg-white"
            id="backup-codes-print"
          >
            {(setup.backup_codes ?? []).map((code) => (
              <div
                key={code}
                className="rounded-sm bg-surface-card px-3 py-2 text-center font-mono text-caption text-ink"
              >
                {code}
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              leadingIcon={
                copiedCodes ? (
                  <CheckCircle2 className="h-4 w-4 text-success-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )
              }
              onClick={() =>
                copyToClipboard((setup.backup_codes ?? []).join("\n"), () =>
                  setCopiedCodes(true),
                )
              }
            >
              {copiedCodes
                ? t("auth.mfa_setup.codes_copied")
                : t("auth.mfa_setup.copy_all_codes")}
            </Button>
            <Button
              variant="secondary"
              leadingIcon={<Printer className="h-4 w-4" />}
              onClick={() => window.print()}
            >
              {t("auth.mfa_setup.print_codes")}
            </Button>
          </div>

          <Button fullWidth size="lg" onClick={() => setStep("verify")}>
            {t("auth.mfa_setup.continue_to_verify")}
          </Button>
        </CardBody>
      </Card>
    );
  }

  const codeError = verifyForm.formState.errors.code?.message
    ? translateFieldError(t, verifyForm.formState.errors.code.message)
    : undefined;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("auth.mfa_setup.verify_title")}</CardTitle>
        <CardSubtitle>{t("auth.mfa_setup.verify_subtitle")}</CardSubtitle>
      </CardHeader>
      <CardBody>
        {topError ? (
          <Alert tone="danger" description={topError} className="mb-5" />
        ) : null}

        <form
          onSubmit={verifyForm.handleSubmit((data) => {
            setTopError(null);
            verifyMutation.mutate({
              code: data.code,
              trust_device: false,
            });
          })}
          className="space-y-4"
          noValidate
        >
          <Input
            label={t("auth.mfa_setup.verify_code_label")}
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            required
            maxLength={6}
            {...verifyForm.register("code")}
            error={codeError}
            className="text-center font-mono text-title tracking-[0.25em]"
          />

          <Button
            type="submit"
            fullWidth
            size="lg"
            isLoading={verifyMutation.isPending}
          >
            {t("auth.mfa_setup.verify_submit")}
          </Button>
        </form>
      </CardBody>
    </Card>
  );
}
